/**
 * NCM and rule descriptions arrive as record data from TIPI, Siscomex and the
 * rules API. Some carry inline markup (`<i>Waffles</i>`), HTML entities
 * (`&amp;`) and the TIPI hierarchy dashes ("-- Bolachas e biscoitos",
 * "- Outros"). Display surfaces show plain text: tags are stripped (never
 * rendered), entities decoded, leading hierarchy dashes and whitespace
 * removed and inner whitespace collapsed.
 *
 * The raw value stays untouched in the API, exports and search.
 */

const NAMED_ENTITIES: Readonly<Record<string, string>> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
  ndash: "–",
  mdash: "—",
  hellip: "…",
  laquo: "«",
  raquo: "»",
  ordm: "º",
  ordf: "ª",
  deg: "°",
  sect: "§",
  middot: "·",
  aacute: "á",
  Aacute: "Á",
  agrave: "à",
  Agrave: "À",
  acirc: "â",
  Acirc: "Â",
  atilde: "ã",
  Atilde: "Ã",
  eacute: "é",
  Eacute: "É",
  ecirc: "ê",
  Ecirc: "Ê",
  iacute: "í",
  Iacute: "Í",
  oacute: "ó",
  Oacute: "Ó",
  ocirc: "ô",
  Ocirc: "Ô",
  otilde: "õ",
  Otilde: "Õ",
  uacute: "ú",
  Uacute: "Ú",
  uuml: "ü",
  Uuml: "Ü",
  ccedil: "ç",
  Ccedil: "Ç",
  ntilde: "ñ",
  Ntilde: "Ñ",
};

/** Inline formatting tags vanish without a gap; any other tag becomes a space. */
const INLINE_TAG = /<\/?(?:i|b|em|strong|u|s|sup|sub|small|span|font|a|abbr|mark)(?:\s[^<>]*)?>/gi;
const TAG = /<\/?[a-zA-Z][^<>]*>|<!--[\s\S]*?-->/g;
const ENTITY = /&(#x[0-9a-fA-F]+|#\d+|[a-zA-Z]+);/g;
/** Hyphen-minus, hyphen, figure/en/em dash, horizontal bar, minus sign. */
const LEADING_DASHES = /^[\s\-‐‑‒–—―−]+/;

function decodeEntity(match: string, body: string): string {
  if (body.startsWith("#")) {
    const code =
      body[1] === "x" || body[1] === "X"
        ? Number.parseInt(body.slice(2), 16)
        : Number.parseInt(body.slice(1), 10);
    if (!Number.isFinite(code) || code <= 0 || code > 0x10ffff) return match;
    return String.fromCodePoint(code);
  }
  return NAMED_ENTITIES[body] ?? match;
}

function stripTags(value: string): string {
  return value.replace(INLINE_TAG, "").replace(TAG, " ");
}

export function formatNcmDescription(raw: string | null | undefined): string {
  if (!raw) return "";
  // Entity-escaped markup (`&lt;i&gt;`) is stripped too, after decoding.
  return stripTags(stripTags(raw).replace(ENTITY, decodeEntity))
    .replace(/\s+/g, " ")
    .replace(LEADING_DASHES, "")
    .trim();
}
